import InsightsTable from "./InsightsTable";
import {
  InsightsKeys,
  insightsTables,
} from "../constants/articlesInsightsConstants";
import {
  InsightsTablesType,
  InsightsOrderByColumn,
  InsightsOrderByColumns,
  InsightsOrderByValues,
  useInsightsData,
} from "../hooks/useInsightsData";
import { FC, useMemo, useState } from "react";
import TotalCount from "./TotalCount";
import styled from "styled-components";
import { InsightsRowValueFiltersType } from "../types/insightsFilterType";
import { InsightsRowType } from "../types/insightsType";
import NoInsightsData from "./NoInsightsData";
import { defaultInsightsOrderByValues } from "../constants/insightsFilters";

const InsightsTablesWrapper = styled.div`
  margin-top: 20px;

  @media (min-width: 950px) {
    padding: 0 24px;
  }
`;

const TotalCountsContainer = styled.div`
  @media (min-width: 950px) {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
  }
`;

const InsightsTables: FC<{
  hasSearchedArticleNo: boolean;
  filters: InsightsRowValueFiltersType;
  handleRowClick: (row: InsightsRowType, table: InsightsTablesType) => void;
  selectedPeriod: string;
  clearArticleSearchInputValue: () => void;
}> = ({
  hasSearchedArticleNo,
  filters,
  handleRowClick,
  selectedPeriod,
  clearArticleSearchInputValue,
}) => {
  const [orderByColumn, setOrderByColumn] = useState<InsightsOrderByColumns>(
    defaultInsightsOrderByValues
  );

  const getOrderByDirection = (
    column: string,
    previousColumn: InsightsOrderByValues
  ) => {
    if (column === previousColumn.column) {
      return previousColumn.direction === "asc" ? "desc" : "asc";
    }
    return "desc";
  };

  const handleColumnClick = (table: InsightsTablesType, column: string) => {
    const newOrderByColumn = {
      ...orderByColumn,
      [table]: {
        column,
        direction: getOrderByDirection(column, orderByColumn[table]),
      },
    };
    setOrderByColumn(newOrderByColumn);
  };

  const [tablePages, setTablePages] = useState({
    articleInsights: 1,
    refilledAreaInsights: 1,
    refilledSalesLocationIDInsights: 1,
    itemHfbTypeInsights: 1,
  });

  const handlePageNavigation = (
    table: InsightsTablesType,
    direction: "next" | "previous"
  ) => {
    setTablePages((prevState) => ({
      ...prevState,
      [table]: prevState[table] + (direction === "next" ? 1 : -1),
    }));
  };

  const resetTablePage = (table: InsightsTablesType) => {
    setTablePages((prevState) => ({
      ...prevState,
      [table]: 1,
    }));
  };

  const insights = useInsightsData(
    tablePages,
    {
      ...filters,
      selectedPeriod,
    },
    orderByColumn
  );

  const hasNoInsightsData = useMemo(() => {
    return (
      (!insights?.totalCounts?.isLoading &&
        !insights?.totalCounts?.data?.pieces) ||
      parseInt(insights?.totalCounts?.data?.pieces ?? "0") === 0
    );
  }, [insights]);

  if (hasNoInsightsData) {
    return (
      <NoInsightsData
        hasSearchedArticleNo={hasSearchedArticleNo}
        clearArticleSearchInputValue={clearArticleSearchInputValue}
      />
    );
  }

  return (
    <InsightsTablesWrapper>
      <TotalCountsContainer>
        <TotalCount
          title="totalPieces"
          count={insights?.totalCounts?.data?.pieces || ""}
          isLoading={insights?.totalCounts.isLoading}
        />
        <TotalCount
          title="totalEstimatedTurnover"
          count={insights?.totalCounts?.data?.estimatedTurnover || ""}
          isLoading={insights?.totalCounts.isLoading}
        />
      </TotalCountsContainer>
      {insightsTables.map((table: InsightsTablesType, index: number) => (
        <InsightsTable
          key={index}
          title={table}
          columns={InsightsKeys[table]}
          data={insights[table]?.data as any}
          onRowClick={(row) => handleRowClick(row, table)}
          onColumnClick={(column: InsightsOrderByColumn) =>
            handleColumnClick(table, column)
          }
          selectedColumn={orderByColumn[table]}
          isLoading={insights[table]?.isLoading}
          handlePageNavigation={handlePageNavigation}
          resetTablePage={resetTablePage}
          hasSearchedArticleNo={hasSearchedArticleNo}
        />
      ))}
    </InsightsTablesWrapper>
  );
};

export default InsightsTables;
